import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { GlobalQueryKeys } from '@enums/react-query-keys';
import { usePreferences } from '@context/preferences.context';
import { useShoppingPreferences } from '@hooks/user/use-shopping-preferences';
import { getStickyNavData } from '@api/static';
import { StickyNavApiResponse, StickyNavApiEntry } from '@interfaces/models/sticky-nav';
import { MEN_UNIVERSE, WOMEN_UNIVERSE } from '@constants/user-shopping-preferences';
import { isFromStickyNav, getStickyCategoryId, getStickyUniverse } from '@helpers/utils/sticky-nav/sticky-nav-utils';

interface UseStickyNavQueryParams {
  universe?: number;
}

const inferUniverseFromStickyCategoryId = (
  stickyCategoryId: string | undefined,
  data: StickyNavApiResponse | undefined,
): number | undefined => {
  if (!stickyCategoryId || !data) {
    return undefined;
  }
  const inMen = data.men?.some((entry) => entry.destination?.categoryId === stickyCategoryId);
  const inWomen = data.women?.some((entry) => entry.destination?.categoryId === stickyCategoryId);
  if (inMen && !inWomen) {
    return MEN_UNIVERSE;
  }
  if (inWomen && !inMen) {
    return WOMEN_UNIVERSE;
  }
  return undefined;
};

const useStickyNavQuery = ({ universe: universeFromPage }: UseStickyNavQueryParams = {}) => {
  const router = useRouter();
  const { siteId, locale } = usePreferences();
  const { preferences, persistedPreferences } = useShoppingPreferences();
  const queryClient = useQueryClient();

  const query = router.query ?? {};
  const stickyCategoryId = getStickyCategoryId(query);
  const stickyUniverseFromUrl = getStickyUniverse(query);
  const fromStickyNav = isFromStickyNav(query);

  const cachedStickyNavData = queryClient.getQueryData<StickyNavApiResponse>([GlobalQueryKeys.STICKY_NAV]);
  const shouldFetch = !cachedStickyNavData;

  const { data: apiResponse } = useQuery<StickyNavApiResponse>({
    queryFn: () => getStickyNavData({ siteId, locale }),
    queryKey: [GlobalQueryKeys.STICKY_NAV],
    cacheTime: Infinity,
    staleTime: Infinity,
    enabled: shouldFetch,
    initialData: cachedStickyNavData,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const dataToUse = apiResponse || cachedStickyNavData;

  const currentUniverse = useMemo(() => {
    if (universeFromPage !== undefined && (universeFromPage === MEN_UNIVERSE || universeFromPage === WOMEN_UNIVERSE)) {
      return universeFromPage;
    }
    if (fromStickyNav && stickyUniverseFromUrl === MEN_UNIVERSE) {
      return MEN_UNIVERSE;
    }
    if (fromStickyNav && stickyUniverseFromUrl === WOMEN_UNIVERSE) {
      return WOMEN_UNIVERSE;
    }
    const fromStickyCategoryId = inferUniverseFromStickyCategoryId(stickyCategoryId, dataToUse);
    if (fromStickyCategoryId) {
      return fromStickyCategoryId;
    }
    return preferences?.universe ?? persistedPreferences?.universe ?? WOMEN_UNIVERSE;
  }, [
    universeFromPage,
    stickyUniverseFromUrl,
    stickyCategoryId,
    fromStickyNav,
    dataToUse,
    preferences?.universe,
    persistedPreferences?.universe,
  ]);

  const stickyNavData = useMemo<StickyNavApiEntry[]>(() => {
    if (!dataToUse) {
      return [];
    }
    return currentUniverse === MEN_UNIVERSE ? dataToUse.men : dataToUse.women;
  }, [currentUniverse, dataToUse]);

  return {
    stickyNavData,
    currentUniverse,
    isLoading: shouldFetch && !apiResponse && !cachedStickyNavData,
  };
};

export default useStickyNavQuery;
